import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage, useField, useFormikContext } from "formik";
import styled from "styled-components";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import {
  registrationInitialValues,
  validationSchema,
} from "./registrationSchema";
import { ErrorText } from "../../../../shared/inputs/ErrorText";
import { signup } from "../../../../../services/users/user-services";
import { getApiErrorMessage, getApiErrorTitle, getSignupSuccessCopy } from "../../../../../utils/apiErrorMessage";
import { getSubscriptions } from "../../../../../services/subscriptions/subscriptions-services";
import { getPlatformStatus } from "../../../../../services/admin/admin-platform-service";
import { useNavigate } from "react-router-dom";
import { FormAlert } from "../../../../shared/alerts/FormAlert";
import { ButtonSpinner } from "../../../../shared/loading/LoadingScreen";
import Modal from "../../../../shared/modal/Modal";
import {
  FALLBACK_DIAL_OPTIONS,
  toFlagEmoji,
} from "../../../../../utils/dialCodes";
import { findLocation } from "../../../../../utils/locations";
import { fetchLocations } from "../../../../../services/locations/locations-service";
// SMS verification disabled temporarily
// import { sendMobileOtp, verifyMobileOtp } from "../../../../../services/phase/phase-service";
import { SubscriptionComparisonTable } from "../../../../shared/sections/subscription-plan/SubscriptionComparisonTable";
import CaptchaField from "../../../../shared/inputs/CaptchaField";
import { fontFamily } from "../../../../shared/styles/theme";
import { primaryColor, white } from "../../../../shared/styles/color";
import { googleLogo } from "../../../../../assets/s3Assets";
import {
  FiBriefcase,
  FiLock,
  FiMail,
  FiPhone,
  FiShield,
  FiUser,
} from "react-icons/fi";
import { FaLinkedinIn } from "react-icons/fa";

const RegistrationForm = () => {
  const navigate = useNavigate();

  const [dialOptions] = useState(FALLBACK_DIAL_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const [hasAgreedToPrivacyPolicy, setHasAgreedToPrivacyPolicy] =
    useState(false);
  const [hasAgreedToMarketing, setHasAgreedToMarketing] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaChallenge, setCaptchaChallenge] = useState({});
  // SMS verification disabled temporarily
  // const [otpSent, setOtpSent] = useState(false);
  // const [otpSending, setOtpSending] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [closedMessage, setClosedMessage] = useState("");
  const [locationOptions, setLocationOptions] = useState([]);
  useEffect(() => {
    fetchLocations().then(setLocationOptions).catch(() => setLocationOptions([]));
  }, []);
  useEffect(() => {
    getPlatformStatus()
      .then((res) => {
        const data = res?.data?.result?.data;
        if (data?.userRegistration === false) {
          setRegistrationClosed(true);
          setClosedMessage(
            data?.registrationMessage ||
              "New user registration is currently disabled. Please check back later.",
          );
        }
      })
      .catch(() => {});
  }, []);

  if (registrationClosed) {
    return (
      <FormCard>
        <FormTitle>Registration Unavailable</FormTitle>
        <ClosedNotice>{closedMessage}</ClosedNotice>
      </FormCard>
    );
  }

  return (
    <FormCard>
      <FormHeader>
        <FormTitle>Sign up to Liber</FormTitle>
        <LoginPrompt>
          Already have an account?{" "}
          <TextLink type="button" onClick={() => navigate("/login")}>
            Log in
          </TextLink>
        </LoginPrompt>
      </FormHeader>

      {formAlert && (
        <FormAlert
          variant={formAlert.variant}
          title={formAlert.title}
          message={formAlert.message}
          onDismiss={() => setFormAlert(null)}
        />
      )}

      <Formik
        initialValues={registrationInitialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setFormAlert(null);

          if (!hasAgreedToPrivacyPolicy) {
            setFormAlert({
              variant: "warning",
              title: "Privacy Policy",
              message: "You must agree to the Privacy Policy!",
            });
            return;
          }
          if (!captchaValid) {
            setFormAlert({
              variant: "warning",
              title: "Verification",
              message: "Please complete the CAPTCHA.",
            });
            return;
          }
          const loc = findLocation(values.location);
          const payload = {
            ...values,
            mobile: `${values.mobile_country_code || ""}${values.mobile_number || ""}`,
            location_city: loc?.city || values.location,
            location_country: loc?.country || "",
            location_lat: loc?.lat ?? null,
            location_lng: loc?.lng ?? null,
            mobile_verified: true,
            hasAgreedToPrivacyPolicy,
            hasAgreedToMarketing,
            ...captchaChallenge,
          };
          delete payload.mobile_country_code;
          delete payload.mobile_number;
          delete payload.otp_code;
          delete payload.location;

          setLoading(true);
          // SMS verification disabled temporarily
          // await verifyMobileOtp(payload.mobile, values.otp_code);
          try {
            const result = await signup(payload);
            if (!result) {
              setFormAlert({
                variant: "danger",
                title: "Network error",
                message:
                  "Could not reach the server. Check your connection and try again.",
              });
              return;
            }

            const status = result.status;
            const apiMessage = getApiErrorMessage(result);

            if (status === 201) {
              const user =
                result?.data?.user ||
                result?.data?.result?.data ||
                result?.data?.result;
              const { title, text } = getSignupSuccessCopy({
                response: result,
                user,
                email: values.email,
              });
              const isFreelancer =
                user?.user_type === "freelancers" ||
                user?.user_type === "freelancer";
              navigate("/login", {
                replace: true,
                state: {
                  registrationSuccess: {
                    title,
                    message: text,
                  },
                  ...(isFreelancer ? { onboardingSubscribe: true } : {}),
                },
              });
              return;
            }

            if (status === 403) {
              setFormAlert({
                variant: "info",
                title: getApiErrorTitle(status, apiMessage),
                message:
                  apiMessage ||
                  "New user registration is currently disabled.",
              });
              return;
            }

            setFormAlert({
              variant: status === 409 ? "warning" : "danger",
              title: getApiErrorTitle(status, apiMessage),
              message: apiMessage,
            });
          } finally {
            setLoading(false);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          return (
            <StyledForm onSubmit={handleSubmit}>
              <RoleTabRow>
                <RoleTab
                  type="button"
                  $active={values.user_type === "freelancers"}
                  onClick={() => setFieldValue("user_type", "freelancers")}
                >
                  <FiUser size={18} />
                  I&apos;m a Freelancer
                </RoleTab>
                <RoleTab
                  type="button"
                  $active={values.user_type === "business"}
                  onClick={() => setFieldValue("user_type", "business")}
                >
                  <FiBriefcase size={18} />
                  I&apos;m a Business
                </RoleTab>
              </RoleTabRow>
              <ErrorMessage name="user_type" component={ErrorText} />

              <NameRow>
                <SignupField
                  name="first_name"
                  placeholder="First Name"
                  icon={FiUser}
                />
                <SignupField
                  name="last_name"
                  placeholder="Last Name"
                  icon={FiUser}
                />
              </NameRow>

              {values.user_type === "business" && (
                <SignupField
                  name="company_name"
                  placeholder="Company name"
                  icon={FiBriefcase}
                />
              )}

              <SignupField
                name="email"
                type="email"
                placeholder="Email Address"
                icon={FiMail}
              />

              <PhoneRow>
                <PhoneCode>
                  <DialCodeField
                    name="mobile_country_code"
                    options={dialOptions}
                  />
                </PhoneCode>
                <PhoneNumber>
                  <SignupField
                    name="mobile_number"
                    type="tel"
                    placeholder="Phone Number"
                    icon={FiPhone}
                    onChange={(e) => {
                      const digitsOnly = String(e.target.value || "").replace(
                        /[^\d]/g,
                        "",
                      );
                      setFieldValue("mobile_number", digitsOnly);
                    }}
                  />
                </PhoneNumber>
              </PhoneRow>
              <ErrorMessage name="mobile_country_code" component={ErrorText} />

              <SignupField
                name="password"
                type="password"
                placeholder="Create Password"
                icon={FiLock}
              />
              <SignupField
                name="password2"
                type="password"
                placeholder="Retype Password"
                icon={FiLock}
              />

              <LocationSelectField
                name="location"
                options={locationOptions.length ? locationOptions : [{ value: "", label: "Loading locations…" }]}
                labelText="Location"
                placeholder="Select your location"
              />

              {/* SMS verification disabled temporarily
              <OtpSection otpSent={otpSent} otpSending={otpSending} setOtpSent={setOtpSent} setOtpSending={setOtpSending} showAlert={showAlert} />
              */}

              {/* Freelancer plan comparison hidden on registration — choose plan after signup
              <FreelancerPlanExplainer />
              */}

              <CaptchaField
                onValidatedChange={setCaptchaValid}
                onChallengeChange={setCaptchaChallenge}
              />

              <CheckboxContainer>
                <ConsentCheckbox
                  type="checkbox"
                  id="privacy_consent"
                  onChange={(event) => {
                    setHasAgreedToPrivacyPolicy(event.target.checked);
                  }}
                />
                <span>
                  I have read and agree to the{" "}
                  <LinkButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsPrivacyModalOpen(true);
                    }}
                  >
                    Privacy Policy
                  </LinkButton>{" "}
                  &amp;{" "}
                  <LinkButton
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsTermsModalOpen(true);
                    }}
                  >
                    Terms &amp; Conditions.
                  </LinkButton>
                </span>
              </CheckboxContainer>
              <Modal
                isOpen={isTermsModalOpen}
                onClose={() => setIsTermsModalOpen(false)}
                title="Terms & Conditions"
                modalContainerWidth="50%"
              >
                <TermsModalContent />
              </Modal>
              <Modal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
                title="Privacy Policy"
                modalContainerWidth="50%"
              >
                <PrivacyModalContent />
              </Modal>
              <ErrorMessage
                name="agree_to_privacy_policy"
                component={ErrorText}
              />
              <CheckboxContainer>
                <ConsentCheckbox
                  type="checkbox"
                  id="marketing_consent"
                  onChange={(event) => {
                    setHasAgreedToMarketing(event.target.checked);
                  }}
                />
                <span>I consent to receive newsletters &amp; marketing updates</span>
              </CheckboxContainer>

              {loading ? (
                <ButtonSpinner />
              ) : (
                <CreateAccountBtn
                  type="submit"
                  disabled={!hasAgreedToPrivacyPolicy}
                >
                  Create Account
                </CreateAccountBtn>
              )}

              {/* Social signup hidden for now
              <DividerRow>
                <DividerLine />
                <DividerText>or sign up with</DividerText>
                <DividerLine />
              </DividerRow>

              <SocialRow>
                <SocialBtn type="button" disabled title="Coming soon">
                  <SocialIcon src={googleLogo} alt="" />
                  Google
                </SocialBtn>
                <SocialBtn type="button" disabled title="Coming soon">
                  <LinkedInIcon aria-hidden>
                    <FaLinkedinIn size={18} />
                  </LinkedInIcon>
                  LinkedIn
                </SocialBtn>
              </SocialRow>
              */}

              <SecurityNote>
                <FiShield size={16} />
                <span>
                  Your information is safe with us. We{" "}
                  <strong>never share</strong> your data.
                </span>
              </SecurityNote>
            </StyledForm>
          );
        }}
      </Formik>
    </FormCard>
  );
};

function SignupField({
  name,
  type = "text",
  placeholder,
  icon: Icon,
  onChange,
}) {
  const [field, meta] = useField(name);
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && showPassword ? "text" : type;

  return (
    <FieldWrap>
      <InputShell>
        {Icon && (
          <LeadingIcon aria-hidden>
            <Icon size={18} />
          </LeadingIcon>
        )}
        <StyledSignupInput
          {...field}
          type={inputType}
          placeholder={placeholder}
          onChange={onChange || field.onChange}
        />
        {isPassword && (
          <TogglePassword
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </TogglePassword>
        )}
      </InputShell>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FieldWrap>
  );
}

function OtpSectionDisabled({ otpSent, otpSending, setOtpSent, setOtpSending, showAlert }) {
  return null;
  /* SMS verification disabled temporarily
  const { values, setFieldValue } = useFormikContext();
  return (
    <>
      <OtpRow>
        <SignupField name="otp_code" type="text" placeholder="SMS verification code" icon={FiShield} maxLength={6} />
        <OtpBtn
          type="button"
          disabled={otpSending}
          onClick={async () => {
            const mobile = `${values.mobile_country_code || ""}${values.mobile_number || ""}`;
            if (!mobile || mobile.length < 10) {
              showAlert({ title: "Phone", message: "Enter a valid phone number first.", variant: "warning" });
              return;
            }
            setOtpSending(true);
            try {
              const res = await sendMobileOtp(mobile);
              setOtpSent(true);
              showAlert({
                title: "Code sent",
                message: res?.data?.result?.dev_code
                  ? "Development mode: check server logs for your code."
                  : "Check your phone for the verification code.",
                variant: "success",
              });
            } catch {
              showAlert({ title: "Error", message: "Could not send verification code.", variant: "error" });
            } finally {
              setOtpSending(false);
            }
          }}
        >
          {otpSent ? "Resend code" : "Send code"}
        </OtpBtn>
      </OtpRow>
      <ErrorMessage name="otp_code" component={ErrorText} />
    </>
  );
  */
}

function FreelancerPlanExplainer() {
  const { values } = useFormikContext();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getSubscriptions(null, "monthly")
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data?.result?.data || res?.data?.data || [];
        if (Array.isArray(rows) && rows.length) {
          setPlans(rows.filter((r) => r.is_active !== false));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (values.user_type !== "freelancers") return null;
  return (
    <PlanExplainerWrap>
      <PlanNote>
        <strong>Freelancer plans</strong> — choose after signup. Start is free; Grow unlocks applications;
        Advance adds top placement and verified eligibility.
      </PlanNote>
      <ComparisonScroll>
        <SubscriptionComparisonTable monthly plans={plans} />
      </ComparisonScroll>
    </PlanExplainerWrap>
  );
}

function LocationSelectField({ name, options = [], labelText = "Location", placeholder = "Select your location" }) {
  const [field, meta] = useField(name);

  return (
    <FieldWrap>
      <SelectShell>
        <FloatingLabel>{labelText}</FloatingLabel>
        <NationalitySelect {...field} value={field.value || ""}>
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NationalitySelect>
        <SelectChevron size={18} aria-hidden />
      </SelectShell>
      {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
    </FieldWrap>
  );
}

export default RegistrationForm;

function dialCodeLabel(opt) {
  return `${toFlagEmoji(opt?.cca2)} ${opt?.value || ""}`.trim();
}

const DialCodeField = ({ name, options = [] }) => {
  const [field, meta, helpers] = useField(name);
  const selectId = `${name}-select`;

  return (
    <DialWrap>
      <DialSelectShell>
        <DialSelect
          id={selectId}
          name={name}
          value={field.value}
          onChange={(e) => helpers.setValue(e.target.value)}
          onBlur={field.onBlur}
          aria-label="Country calling code"
          aria-invalid={meta?.touched && meta.error ? "true" : "false"}
        >
          {options.map((opt) => (
            <option
              key={`${opt.cca2 || "x"}-${opt.value}`}
              value={opt.value}
              title={opt.label}
            >
              {dialCodeLabel(opt)}
            </option>
          ))}
        </DialSelect>
        <DialChevron size={18} aria-hidden />
      </DialSelectShell>
    </DialWrap>
  );
};

const PhoneRow = styled.div`
  display: grid;
  grid-template-columns: minmax(108px, 128px) 1fr;
  gap: 12px;
  width: 100%;
  align-items: start;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const PhoneCode = styled.div`
  width: 100%;
`;

const PhoneNumber = styled.div`
  width: 100%;
`;

const DialWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DialSelectShell = styled.div`
  position: relative;
  width: 100%;
`;

const DialSelect = styled.select`
  width: 100%;
  padding: 13px 28px 13px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 500;
  background: ${white};
  appearance: none;
  cursor: pointer;
  outline: none;
  color: #111827;
  box-sizing: border-box;
  min-height: 48px;

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const DialChevron = styled(ChevronDown)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  pointer-events: none;
`;

const ConsentCheckbox = styled.input`
  height: 18px;
  width: 18px;
  min-width: 18px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  background: white;
  cursor: pointer;
  flex-shrink: 0;
  margin-top: 2px;
  accent-color: ${primaryColor};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

export const Container = styled.div`
  min-height: 35rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  text-align: start;
`;

const OtpRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  & > div:first-child { flex: 1; }
  @media (max-width: 520px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const OtpBtn = styled.button`
  margin-top: 28px;
  padding: 10px 14px;
  border: 1px solid ${primaryColor};
  border-radius: 8px;
  background: ${white};
  color: ${primaryColor};
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  @media (max-width: 520px) {
    margin-top: 0;
    width: 100%;
  }
`;

const PlanExplainerWrap = styled.div`
  margin: 8px 0 4px;
`;

const ComparisonScroll = styled.div`
  overflow-x: auto;
  margin-top: 8px;
  -webkit-overflow-scrolling: touch;
`;

const PlanNote = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 8px 0 12px;
`;

const FormCard = styled.div`
  background: ${white};
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px 28px 24px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  width: 100%;
  box-sizing: border-box;
  text-align: left;

  @media (max-width: 767px) {
    padding: 22px 18px 20px;
    border-radius: 12px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 22px;
`;

const FormTitle = styled.h1`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  color: #111827;
  margin: 0 0 8px;
  text-align: left;
`;

const LoginPrompt = styled.p`
  font-family: ${fontFamily.font};
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  text-align: left;
`;

const TextLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${primaryColor};
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Title = FormTitle;

const ClosedNotice = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
  max-width: 520px;
`;

export const StyledForm = styled(Form)`
  width: 100%;
  display: flex;
  gap: 14px;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
`;

const RoleTabRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const RoleTab = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 10px;
  border-radius: 10px;
  border: 1.5px solid ${(p) => (p.$active ? primaryColor : "#e5e7eb")};
  background: ${white};
  color: ${(p) => (p.$active ? primaryColor : "#6b7280")};
  font-family: ${fontFamily.font};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;

  svg {
    flex-shrink: 0;
  }
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InputShell = styled.div`
  position: relative;
  width: 100%;
`;

const LeadingIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
`;

const StyledSignupInput = styled.input`
  width: 100%;
  padding: 13px 14px 13px 42px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 400;
  line-height: 1.4;
  outline: none;
  box-sizing: border-box;
  background: ${white};
  color: #111827;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const TogglePassword = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;

  &:hover {
    color: #6b7280;
  }
`;

const SelectShell = styled.div`
  position: relative;
  width: 100%;
`;

const FloatingLabel = styled.span`
  position: absolute;
  top: -8px;
  left: 12px;
  font-size: 11px;
  font-weight: 500;
  color: #9ca3af;
  background: ${white};
  padding: 0 4px;
  font-family: ${fontFamily.font};
  z-index: 1;
`;

const NationalitySelect = styled.select`
  width: 100%;
  padding: 14px 36px 14px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-family: ${fontFamily.font};
  font-weight: 400;
  background: ${white};
  appearance: none;
  cursor: pointer;
  outline: none;
  color: #111827;
  box-sizing: border-box;
  min-height: 48px;

  &:focus {
    border-color: ${primaryColor};
    box-shadow: 0 0 0 3px rgba(17, 138, 178, 0.1);
  }
`;

const SelectChevron = styled(ChevronDown)`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const CreateAccountBtn = styled.button`
  width: 100%;
  padding: 14px 20px;
  border: 0;
  border-radius: 10px;
  background: ${primaryColor};
  color: ${white};
  font-family: ${fontFamily.font};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
  }
`;

const DividerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 4px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`;

const DividerText = styled.span`
  font-family: ${fontFamily.font};
  font-size: 13px;
  color: #9ca3af;
  white-space: nowrap;
`;

const SocialRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
`;

const SocialBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: ${white};
  font-family: ${fontFamily.font};
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;

  &:disabled {
    opacity: 0.85;
    cursor: not-allowed;
  }
`;

const SocialIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const LinkedInIcon = styled.span`
  color: #0a66c2;
  display: flex;
  align-items: center;
`;

const SecurityNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${fontFamily.font};
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;
  margin-top: 4px;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${primaryColor};
  }

  strong {
    color: ${primaryColor};
    font-weight: 600;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  text-align: left;

  span {
    font-family: ${fontFamily.font};
    font-size: 13px;
    line-height: 1.5;
    color: #374151;
    flex: 1;
    min-width: 0;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #118ab2;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  margin: 0;
  &:hover {
    color: #0d6b8a;
  }
`;

const ModalContentContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding: 24px;
  font-family: "Poppins", sans-serif;
  background: #ffffff;
  text-align: left !important;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f5f5f5;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 3px;
    
    &:hover {
      background: #a0a0a0;
    }
  }
  
  /* Force all headings to be left-aligned */
  h1, h2, h3, h4, h5, h6 {
    text-align: left !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    display: block !important;
  }
`;

const ModalHeading1 = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
  color: #1d1e2b;
  border-bottom: 2px solid #118ab2;
  padding-bottom: 12px;
  text-align: left !important;
  display: block;
  width: 100%;
`;

const ModalHeading2 = styled.h2`
  font-size: 15px;
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 10px;
  margin-left: 0;
  margin-right: 0;
  color: #2c3e50;
  line-height: 1.4;
  text-align: left !important;
  display: block;
  width: 100%;
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const ModalText = styled.p`
  font-size: 13px;
  line-height: 1.8;
  margin-bottom: 12px;
  color: #4a5568;
  text-align: justify;
  
  strong {
    color: #1d1e2b;
    font-weight: 600;
  }
`;

const TermsModalContent = () => (
  <ModalContentContainer>
    <ModalHeading1>Terms & Conditions</ModalHeading1>
    <ModalText>Effective Date: 27/01/2026</ModalText>
    <ModalText>Last Updated: 27/01/2026</ModalText>
    <ModalText>
      These Terms & Conditions ("Terms") govern your access to and use of the
      Liber platform (the "Platform"), operated by Liber FZE, a company
      registered in Sharjah Free Zone, United Arab Emirates. By registering or
      using Liber, you agree to be bound by these Terms.
    </ModalText>

    <ModalHeading2>1. Eligibility</ModalHeading2>
    <ModalText>
      - You must be at least 18 years old and legally capable of entering into a
      binding contract to use Liber.
    </ModalText>
    <ModalText>
      - Freelancers worldwide may register, subject to compliance with
      applicable laws.
    </ModalText>
    <ModalText>
      - Businesses from both the UAE and internationally may register and engage
      freelancers.
    </ModalText>

    <ModalHeading2>2. Services Provided</ModalHeading2>
    <ModalText>
      Liber operates an online marketplace that facilitates connections between
      freelancers with businesses. Liber does not itself provide freelance
      services or act as an employer, agent, or representative of any user. The
      Platform includes:
    </ModalText>
    <ModalText>- Freelancer profiles and discovery.</ModalText>
    <ModalText>
      - Subscriptions for freelancers (different service tiers).
    </ModalText>
    <ModalText>- Business project postings and hiring tools.</ModalText>
    <ModalText>- Secure payments processed through Stripe UAE.</ModalText>
    <ModalText>
      - Dispute resolution services for project-related issues.
    </ModalText>
    <ModalText>- Sponsored content and advertising placements.</ModalText>

    <ModalHeading2>3. Accounts</ModalHeading2>
    <ModalText>
      - Users must provide accurate and truthful information.
    </ModalText>
    <ModalText>
      - Vetted freelancers may be required to provide government ID (e.g.,
      Emirates ID, passport) and other certifications or credentials relevant to
      their services for verification.
    </ModalText>
    <ModalText>
      - Liber does not verify the quality, suitability, or legality of services
      offered by freelancers and does not guarantee the accuracy of
      user-provided information.
    </ModalText>
    <ModalText>
      - Liber reserves the right to suspend or terminate accounts for
      violations of these Terms.
    </ModalText>

    <ModalHeading2>4. Subscriptions, Fees & Payments</ModalHeading2>
    <ModalText>
      - Freelancers: Pay subscription fees to access premium features (tiers as
      published on the Platform). In addition, when a freelancer is paid by a
      client, Liber charges a 15% commission on the total payment amount before
      the funds are released to the freelancer.
    </ModalText>
    <ModalText>
      - Businesses: No subscription fees; however, a transaction fee is charged
      per project.
    </ModalText>
    <ModalText>
      - Payments: Businesses pay freelancers through the Platform. All payments
      are processed by Stripe UAE, a third-party payment service provider. Funds
      are held by Stripe until project completion and approval by the business.
      Liber does not hold client funds or act as a payment processor.
    </ModalText>
    <ModalText>
      - Refunds and disputes: Stripe processes payment transactions, refunds,
      and chargebacks in accordance with its own terms and policies. Liber may
      facilitate or mediate disputes relating to work quality but does not
      guarantee outcomes and does not assume liability for decisions made by
      Stripe or by users, and resolutions may affect payment release.
    </ModalText>

    <ModalHeading2>5. User Conduct</ModalHeading2>
    <ModalText>Users must not:</ModalText>
    <ModalText>
      - Post illegal, fraudulent, misleading, or harmful content.
    </ModalText>
    <ModalText>- Harass, abuse, or discriminate against others.</ModalText>
    <ModalText>
      - Post spam, malware, or unauthorized advertising.
    </ModalText>
    <ModalText>- Circumvent Liber's systems to avoid fees.</ModalText>
    <ModalText>
      Liber reserves the right to suspend or terminate accounts for misconduct
      or breaches of these Terms.
    </ModalText>
    <ModalText>
      Liber may remove content or restrict access where necessary to comply with
      applicable law or protect the integrity of the Platform.
    </ModalText>

    <ModalHeading2>6. Intellectual Property</ModalHeading2>
    <ModalText>
      - Freelancers retain ownership of intellectual property in their work
      unless otherwise agreed in writing between the freelancer and the business.
    </ModalText>
    <ModalText>
      - Upon full payment, the business receives rights to use a non-exclusive,
      perpetual, worldwide licence to use the deliverables for their intended
      purpose, unless otherwise agreed between the parties.
    </ModalText>
    <ModalText>
      - Liber does not claim ownership of user content but is granted a limited,
      non-exclusive license to host, display, and promote such content on the
      Platform for operational and marketing purposes (e.g., for profiles,
      portfolio showcases, or marketing).
    </ModalText>

    <ModalHeading2>7. Sponsored Content & Marketing</ModalHeading2>
    <ModalText>
      - Liber may display sponsored content and advertisements on the Platform.
    </ModalText>
    <ModalText>
      - By registering, users consent to receive newsletters, promotional
      emails, and marketing communications. Users may opt out at any time.
    </ModalText>

    <ModalHeading2>8. Limitation of Liability</ModalHeading2>
    <ModalText>
      - Liber acts solely as an intermediary and is not a party to contracts
      between freelancers and businesses.
    </ModalText>
    <ModalText>
      - Liber is not responsible for user conduct, project outcomes, the quality
      of services provided by freelancers, loss of data, or external links.
    </ModalText>
    <ModalText>
      - The Platform is provided on an "as is" and "as available" basis, without
      warranties of any kind.
    </ModalText>
    <ModalText>
      - To the maximum extent permitted under applicable law, Liber shall not be
      liable for any indirect, incidental, consequential, or economic losses.
      Liber's total liability shall be limited to the fees paid by the relevant
      user to Liber in the six (6) months preceding the claim.
    </ModalText>

    <ModalHeading2>9. Dispute Resolution</ModalHeading2>
    <ModalText>- Payment/refund disputes are processed by Stripe.</ModalText>
    <ModalText>
      - Work quality disputes may be mediated by Liber; decisions made in good
      faith are final.
    </ModalText>
    <ModalText>
      - Any legal dispute, claim, or controversy arising out of or in connection
      with these Terms (including any non-contractual obligations) shall be
      governed by the laws of the Dubai International Financial Centre (DIFC)
      and subject to the jurisdiction of the courts of the DIFC.
    </ModalText>

    <ModalHeading2>10. Termination</ModalHeading2>
    <ModalText>
      Liber may suspend or terminate your account at any time, with or without
      notice, if you breach these Terms or if required by law.
    </ModalText>

    <ModalHeading2>11. Updates to These Terms</ModalHeading2>
    <ModalText>
      Liber may update these Terms from time to time. Users will be notified of
      material changes through the Platform or by other reasonable means.
      Continued use of Liber after changes constitutes acceptance of the new
      Terms.
    </ModalText>

    <ModalHeading2>12. Contact</ModalHeading2>
    <ModalText>
      If you have any questions about these Terms please contact us at:
    </ModalText>
    <ModalText>Liber FZE</ModalText>
    <ModalText>Sharjah Free Zone, UAE</ModalText>
    <ModalText>Email: info@liber-x.com</ModalText>
  </ModalContentContainer>
);

const PrivacyModalContent = () => (
  <ModalContentContainer>
    <ModalHeading1>Privacy Policy</ModalHeading1>
    <ModalText>Effective Date: 27/01/2026</ModalText>
    <ModalText>Last Updated: 27/01/2026</ModalText>

    <ModalHeading2>1. Who We Are</ModalHeading2>
    <ModalText>
      This Privacy Policy applies to Liber FZE ("Liber", "we", "our", "us"), a
      company registered in Sharjah Free Zone, United Arab Emirates, which
      operates the Liber online platform (the "Platform").
    </ModalText>
    <ModalText>
      We provide a freelance marketplace that connects freelancers and
      businesses, with additional services including subscriptions, Liber Learn
      (third-party digital courses), a freelancer reward scheme, and advertising
      opportunities.
    </ModalText>
    <ModalText>
      For the purposes of applicable data protection laws, including UAE Federal
      Decree-Law No. 45 of 2021 on the Protection of Personal Data (the "PDPL"),
      Liber FZE acts as the data controller in respect of personal data
      processed through the Platform.
    </ModalText>
    <ModalText>Contact email: info@liber-x.com</ModalText>

    <ModalHeading2>2. Scope</ModalHeading2>
    <ModalText>
      This Privacy Policy applies to the processing of personal data by Liber in
      connection with the operation of the Platform, including personal data
      processed through:
    </ModalText>
    <ModalText>- The Platform (website and mobile app).</ModalText>
    <ModalText>- Freelancer and business user accounts.</ModalText>
    <ModalText>- Identity verification for vetted freelancers.</ModalText>
    <ModalText>
      - Payment processing services provided via Stripe UAE.
    </ModalText>
    <ModalText>- User support and dispute resolution.</ModalText>
    <ModalText>
      - Marketing communications (including emails and newsletters).
    </ModalText>
    <ModalText>
      - Advertising and sponsored content hosted on the Platform.
    </ModalText>

    <ModalHeading2>3. Information We Collect</ModalHeading2>
    <ModalText>
      <strong>A. Information You Provide</strong>
    </ModalText>
    <ModalText>
      We may collect personal data that you voluntarily provide to us when you
      register for or use the Platform, including:
    </ModalText>
    <ModalText>
      - Account Information: name, email address, telephone number, password,
      profile photo, biography, work history, skills, and portfolios.
    </ModalText>
    <ModalText>
      - Business Information: company name and details, project postings, and
      hiring preferences.
    </ModalText>
    <ModalText>
      - Communications: proposals, messages, attachments, reviews, feedback, and
      communications with our support team.
    </ModalText>
    <ModalText>
      - Verification (vetted freelancers): copies of government issued
      identification documents (e.g., Emirates ID or passport) and other
      information reasonably required for verification purposes.
    </ModalText>
    <ModalText>
      - Liber Learn purchases: when you purchase third-party digital courses,
      you will be redirected to the relevant third-party provider. Liber does
      not process or store course content or payment details relating to such
      purchases.
    </ModalText>
    <ModalText>
      <strong>B. Information Collected Automatically</strong>
    </ModalText>
    <ModalText>
      When you access or use the Platform, we may automatically collect certain
      technical and usage information, including:
    </ModalText>
    <ModalText>
      - IP address, browser type, operating system, and device identifiers.
    </ModalText>
    <ModalText>
      - Usage data such as access time, clicks, pages viewed, referrals, and
      session duration.
    </ModalText>
    <ModalText>
      - Information collected through Cookies and similar analytics tools, as
      described in our Cookie Policy (https://liber-x.com/cookie-policy).
    </ModalText>
    <ModalText>
      <strong>C. Payment Data</strong>
    </ModalText>
    <ModalText>
      - All payments made through the Platform are processed by Stripe UAE, a
      third-party payment service provider.
    </ModalText>
    <ModalText>
      - Liber does not collect, store, or have access to users' full card or
      bank account details.
    </ModalText>
    <ModalText>
      - Stripe processes payment transactions, refunds, and chargebacks in
      accordance with its own terms and policies. Liber may facilitate or
      mediate disputes relating to work quality but does not process payments or
      refunds.
    </ModalText>
    <ModalText>
      <strong>D. Third Parties</strong>
    </ModalText>
    <ModalText>
      We may receive or share personal data in connection with the Platform with
      the following categories of third parties:
    </ModalText>
    <ModalText>
      - Advertising and sponsorship partners (in connection with sponsored
      content).
    </ModalText>
    <ModalText>
      - Analytics, hosting, and customer support service providers.
    </ModalText>
    <ModalText>
      - Other service providers engaged to support the operation and security of
      the Platform.
    </ModalText>
    <ModalText>
      Such third parties process personal data either as independent data
      controllers or as processors acting on our instructions, depending on the
      nature of the service provided.
    </ModalText>

    <ModalHeading2>4. How We Use Your Data</ModalHeading2>
    <ModalText>
      We process your personal data for the following purposes in connection
      with the operation of the Platform to:
    </ModalText>
    <ModalText>
      1. Operate, maintain and administer the Platform, including user accounts
      and profiles.
    </ModalText>
    <ModalText>
      2. Verify the identity of vetted freelancers through identification
      checks.
    </ModalText>
    <ModalText>
      3. To facilitate matching and engagement between freelancers and
      businesses.
    </ModalText>
    <ModalText>
      4. To facilitate processing secure payment transactions through Stripe
      UAE.
    </ModalText>
    <ModalText>
      5. To provide customer support and to facilitate the resolution of
      disputes between users and freelancers.
    </ModalText>
    <ModalText>
      6. To send service-related communications, updates, and where consent has
      been given, marketing communications (from which you may opt out).
    </ModalText>
    <ModalText>
      7. To display advertising and sponsored content on the Platform.
    </ModalText>
    <ModalText>
      8. To monitor, analyse, and improve the performance and functionality of
      the Platform through analytics and feedback.
    </ModalText>
    <ModalText>
      9. To comply with applicable UAE laws, regulations and lawful requests from
      authorities.
    </ModalText>

    <ModalHeading2>5. Legal Basis for Processing</ModalHeading2>
    <ModalText>
      Liber processes personal data in accordance with applicable data
      protection laws, including the PDPL, on the following bases:
    </ModalText>
    <ModalText>
      - Contractual necessity: where processing is necessary to provide and
      operate the Platform, including managing user accounts, facilitating
      engagements between freelancers and businesses, and facilitating payment
      transactions through Stripe UAE.
    </ModalText>
    <ModalText>
      - Consent: where users have provided consent, including for marketing
      communications and the use of non-essential cookies.
    </ModalText>
    <ModalText>
      - Legal obligation: where processing is required to comply with applicable
      UAE laws and regulatory requirements, including tax, commercial
      record-keeping, and identity verification where required by competent
      authorities.
    </ModalText>
    <ModalText>
      - Legitimate interests: where processing is necessary for purposes such as
      fraud prevention, platform security, community safety, and improving the
      functionality and performance of the Platform, provided that such
      interests are not overridden by users' rights.
    </ModalText>

    <ModalHeading2>6. Data Sharing & Disclosure</ModalHeading2>
    <ModalText>
      We may share personal data, where necessary for the operation of the
      Platform, with the following categories of recipients:
    </ModalText>
    <ModalText>
      - Other users, limited to information made visible through profiles,
      proposals, and user communications.
    </ModalText>
    <ModalText>
      - Stripe UAE, in connection with payment transactions, refunds, and
      chargebacks processed through the Platform.
    </ModalText>
    <ModalText>
      - Third-party course providers, in connection with Liber Learn offerings.
    </ModalText>
    <ModalText>
      - Service providers engaged to support the operation of the Platform,
      including hosting, analytics, support, advertising, and customer support
      providers.
    </ModalText>
    <ModalText>
      - Regulators, auditors, courts, or other competent authorities, where
      required to comply with applicable law or lawful requests.
    </ModalText>
    <ModalText>
      - Buyers or successors in the event of a merger, acquisition,
      reorganisation, or sale of all or part of Liber's business, subject to
      applicable data protection laws.
    </ModalText>
    <ModalText>
      Such recipients process personal data either as independent data
      controllers or as processors acting on our instructions, depending on the
      nature of the relationship.
    </ModalText>
    <ModalText>
      Liber does not sell personal data. References to rights or disclosures
      applicable under non-UAE data protection laws (such as California consumer
      laws) are included only where and to the extent such laws become
      applicable.
    </ModalText>

    <ModalHeading2>7. Data Retention</ModalHeading2>
    <ModalText>
      Liber retains personal data only for as long as is necessary to fulfil the
      purposes for which it was collected, including to operate the Platform,
      comply with legal obligations, and resolve disputes. Retention periods
      include:
    </ModalText>
    <ModalText>
      - Inactive accounts: personal data relating to inactive user accounts may
      be deleted or anonymised after a reasonable period of inactivity,
      typically between 12 and 24 months.
    </ModalText>
    <ModalText>
      - Support tickets: retained for a reasonable period, typically between 12
      and 24 months, to enable issue resolution and record-keeping.
    </ModalText>
    <ModalText>
      - Financial records: retained for the period required under applicable UAE
      laws and regulations currently up to 5 years.
    </ModalText>
    <ModalText>
      - Verification documents: retained only for as long as reasonably
      necessary to complete verification and comply with applicable legal or
      regulatory requirements.
    </ModalText>
    <ModalText>
      - Analytics data: retained in anonymized or aggregated form where
      possible.
    </ModalText>

    <ModalHeading2>8. Security</ModalHeading2>
    <ModalText>
      Liber implements appropriate technical and organisational measures
      designed to protect personal data against unauthorised access, loss,
      misuse, alteration, or disclosure. Such measures include:
    </ModalText>
    <ModalText>
      - The use of encryption and other security controls for data transmission
      and storage, where appropriate.
    </ModalText>
    <ModalText>
      - The use of Stripe UAE, which processes payment data in accordance with
      applicable PCI DSS standards.
    </ModalText>
    <ModalText>
      - Restricted access to personal data, limited to authorised personnel with
      a legitimate business need.
    </ModalText>
    <ModalText>
      - Monitoring for security incidents, fraud and unauthorised activity, and
      maintaining procedures for responding to such incidents.
    </ModalText>
    <ModalText>
      - Periodic review of service providers and platform security practices.
    </ModalText>

    <ModalHeading2>9. Your Rights</ModalHeading2>
    <ModalText>
      Subject to applicable data protection laws, including the PDPL, users may
      have certain rights in relation to their personal data, which may include
      the right to:
    </ModalText>
    <ModalText>
      - Request access to personal data held about you.
    </ModalText>
    <ModalText>
      - Request correction of inaccurate personal data.
    </ModalText>
    <ModalText>
      - Request deletion of personal data where there is no lawful basis for
      continued processing.
    </ModalText>
    <ModalText>
      - Object to or restrict certain processing activities, where permitted by
      applicable law.
    </ModalText>
    <ModalText>
      - Withdraw consent for processing activities based on consent (such as
      marketing communications or profiling).
    </ModalText>
    <ModalText>
      Additional rights (such as data portability) may apply in limited
      circumstances depending on the jurisdiction and applicable legal
      framework.
    </ModalText>
    <ModalText>
      Request process: send an email to info@liber-x.com. We will verify your
      identity via your account email before acting.
    </ModalText>

    <ModalHeading2>10. Children's Privacy</ModalHeading2>
    <ModalText>
      The Platform is intended for use by individuals 18 and over. Liber does
      not knowingly collect or process personal data relating to individuals
      under the age of 18. If we become aware that personal data of a minor has
      been collected, we will take reasonable steps to delete such data in
      accordance with applicable law.
    </ModalText>

    <ModalHeading2>11. Automated Decision-Making</ModalHeading2>
    <ModalText>
      Liber uses automated tools and algorithms to support certain Platform
      functions, such as matching freelancers with businesses and reward scheme
      calculations. These processes are designed to support Platform
      functionality and do not produce legal or similarly significant effects on
      users.
    </ModalText>
    <ModalText>
      You may contact us to raise questions or request further information
      regarding such processes.
    </ModalText>

    <ModalHeading2>12. Cookies & Analytics</ModalHeading2>
    <ModalText>
      We use cookies to support the operation of the Platform, including:
    </ModalText>
    <ModalText>
      - Essential cookies: which are necessary for core functionality such as
      login, security, and fraud prevention.
    </ModalText>
    <ModalText>
      - Analytics cookies: which help us to understand how users interact with
      the Platform and improve our services.
    </ModalText>
    <ModalText>
      - Advertising cookies: which may be used to display sponsored content and,
      where applicable, enable third-party advertising or tracking.
    </ModalText>
    <ModalText>
      Non-essential cookies are used only where users have provided consent.
      Users may manage their cookie preferences through browser settings or
      other opt-out mechanisms made available on the Platform. Further
      information is set out in our Cookie Policy.
    </ModalText>

    <ModalHeading2>13. Data Transfers</ModalHeading2>
    <ModalText>
      Personal data may be transferred outside the UAE where required for the
      operation of the Platform, including to service providers such as hosting
      or analytics providers. Any such transfers take place in accordance with
      applicable legal requirements and the practices of the relevant service
      providers.
    </ModalText>

    <ModalHeading2>14. Breach Response</ModalHeading2>
    <ModalText>
      In the event of a personal data breach, Liber will assess the incident
      and, where required under applicable UAE law, notify the relevant UAE
      authority and affected users without undue delay.
    </ModalText>

    <ModalHeading2>15. Updates</ModalHeading2>
    <ModalText>
      We may update this Privacy Policy from time to time to reflect changes to
      the Platform, applicable laws, or our data processing practices. Where
      changes are material, we will take reasonable steps to notify users
      through the Platform or by other appropriate means.
    </ModalText>

    <ModalHeading2>16. Contact</ModalHeading2>
    <ModalText>
      If you have any questions about this Privacy Policy or our data processing
      practices, or wish to exercise your rights under applicable data
      protection laws, please contact us at:
    </ModalText>
    <ModalText>Liber FZE</ModalText>
    <ModalText>Sharjah Free Zone, UAE</ModalText>
    <ModalText>Email: info@liber-x.com</ModalText>
  </ModalContentContainer>
);
